import { useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import type { RecoveryFlow } from '@ory/client'

import { fetchRecoveryFlow } from '@utils/kratos.utils'
import { logClientError, translateClientError } from '@i18n/kratos/kratos-client-error'

interface State {
  flow: RecoveryFlow | null
  error: string | null
  loading: boolean
}

const INITIAL_STATE: State = { flow: null, error: null, loading: true }

// Sibling of useSettingsFlow — keep the intlRef pattern in sync if either
// hook's behaviour around locale changes shifts. intl is a fresh IntlShape
// on every LanguageSwitcher click; reading through a ref keeps the effect
// from re-fetching the same flow on every locale toggle and lets the async
// resolves format errors in the user's current locale, not the one
// captured at fetch-start.
export const useRecoveryFlow = (flowId: string | null, initUrl: string): State => {
  const intl = useIntl()
  const intlRef = useRef(intl)
  useEffect(() => {
    intlRef.current = intl
  }, [intl])

  const [state, setState] = useState<State>(INITIAL_STATE)

  useEffect(() => {
    setState(INITIAL_STATE)

    if (!flowId) {
      window.location.replace(initUrl)
      return undefined
    }

    const controller = new AbortController()
    fetchRecoveryFlow(flowId, controller.signal).then((result) => {
      if (controller.signal.aborted) {
        return
      }
      switch (result.kind) {
        case 'ok':
          setState({ flow: result.flow, error: null, loading: false })
          break
        case 'expired':
          window.location.replace(initUrl)
          break
        case 'error':
          logClientError(result.code, result.message)
          setState({
            flow: null,
            error: translateClientError(intlRef.current, result.code),
            loading: false,
          })
          break
      }
    })
    return () => {
      controller.abort()
    }
  }, [flowId, initUrl])

  return state
}
